import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import Leaderboard from './components/Leaderboard';
import UserProfile from './components/UserProfile';
import Raffle from './components/Raffle';
import { apiService} from './services/cloudflareAPI';

const categories = [
  { id: 'general', name: 'General' },
  { id: 'yourpost', name: 'X (Twitter)' },
  { id: 'comment', name: 'Comments' },
  { id: 'cmc', name: 'CoinMarketCap' },
  { id: 'referral', name: 'Referral' }
];

function AppContent() {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('general');
  const [selectedUser, setSelectedUser] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(false);

  useEffect(() => {
    fetchLeaderboardData();
  }, []);

  const fetchLeaderboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading general leaderboard...');
      const response = await apiService.getLeaderboard('all', 100, 0); // Reduced from 1000 to 100 for faster initial load
      
      // Transform API response to match your component's expected format
      let leaderboardEntries = [];
      if (Array.isArray(response)) {
        leaderboardEntries = response;
      } else if (response.data && Array.isArray(response.data)) {
        leaderboardEntries = response.data;
      }
      
      // Transform entries to include new User interface fields
      const transformedData = leaderboardEntries.map((entry, index) => ({
        // New User interface fields
        id: entry.id || entry.telegram_id,
        telegram_id: entry.telegram_id,
        username: entry.username || `User_${entry.id}`,
        total_xp: entry.total_xp || 0,
        referral_xp: entry.referral_xp || 0,
        bonus_xp: entry.bonus_xp || 0,
        submissions_count: entry.submissions_count || entry.submission_count || 0,
        banned: entry.banned || false,
        
        // Legacy compatibility fields
        totalXP: entry.total_xp || 0, // total_xp already includes all XP types
        submissions: entry.submissions_count || entry.submission_count || 0,
        rank: entry.rank || index + 1,
        referralXP: entry.referral_xp || 0,
        bonusXP: entry.bonus_xp || 0
      }));
      
      const data = {
        general: transformedData,
        yourpost: [],
        comment: [],
        cmc: [],
        referral: []
      };

      setLeaderboardData(data);
      console.log('General leaderboard loaded with', transformedData.length, 'entries!');
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      setError(`Failed to load leaderboard data: ${error.message}`);
      setLeaderboardData({
        general: [],
        yourpost: [],
        comment: [],
        cmc: [],
        referral: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryIfNeeded = async (categoryId) => {
    if (categoryId === 'general' || leaderboardData[categoryId]?.length > 0) {
      return; // Already loaded or not needed
    }
    
    setCategoryLoading(true);
    try {
      console.log(`Loading ${categoryId} data using optimized API calls...`);
      
      let categoryData = [];
      // Use category-specific leaderboard API calls for much faster loading
      switch (categoryId) {
        case 'yourpost':
          console.log('Fetching Twitter/X leaderboard data...');
          const twitterResponse = await apiService.getLeaderboard('all', 100, 0, 'twitter_bonus');
          const twitterData = Array.isArray(twitterResponse) ? twitterResponse : twitterResponse.data || [];
          
          categoryData = twitterData
            .filter(user => !user.banned && user.submissions_count > 0)
            .map(user => ({
              id: user.id || user.telegram_id,
              username: user.username,
              total_xp: (user.submission_xp || 0) + (user.bonus_xp || 0), // Display category-specific XP + bonus
              totalXP: (user.submission_xp || 0) + (user.bonus_xp || 0), // Use submission XP + bonus for category sorting
              submissions: user.submissions_count || 0,
              submissions_count: user.submissions_count || 0,
              submission_xp: user.submission_xp || 0,
              referral_xp: user.referral_xp || 0,
              bonus_xp: user.bonus_xp || 0,
              actual_total_xp: user.total_xp || 0 // Keep actual total for reference
            }))
            .sort((a, b) => b.totalXP - a.totalXP);
          break;
          
        case 'comment':
          console.log('Fetching Comment leaderboard data...');
          const commentResponse = await apiService.getLeaderboard('all', 100, 0, 'comment_bonus');
          const commentData = Array.isArray(commentResponse) ? commentResponse : commentResponse.data || [];
          
          categoryData = commentData
            .filter(user => !user.banned && user.submissions_count > 0)
            .map(user => ({
              id: user.id || user.telegram_id,
              username: user.username,
              total_xp: (user.submission_xp || 0) + (user.bonus_xp || 0), // Display category-specific XP + bonus
              totalXP: (user.submission_xp || 0) + (user.bonus_xp || 0),
              submissions: user.submissions_count || 0,
              submissions_count: user.submissions_count || 0,
              submission_xp: user.submission_xp || 0,
              referral_xp: user.referral_xp || 0,
              bonus_xp: user.bonus_xp || 0,
              actual_total_xp: user.total_xp || 0 // Keep actual total for reference
            }))
            .sort((a, b) => b.totalXP - a.totalXP);
          break;
          
        case 'cmc':
          console.log('Fetching CoinMarketCap leaderboard data...');
          const cmcResponse = await apiService.getLeaderboard('all', 100, 0, 'cmc');
          const cmcData = Array.isArray(cmcResponse) ? cmcResponse : cmcResponse.data || [];

          categoryData = cmcData
            .filter(user => !user.banned && user.submissions_count > 0)
            .map(user => ({
              id: user.id || user.telegram_id,
              username: user.username,
              total_xp: (user.submission_xp || 0) + (user.bonus_xp || 0), // Display category-specific XP + bonus
              totalXP: (user.submission_xp || 0) + (user.bonus_xp || 0),
              submissions: user.submissions_count || 0,
              submissions_count: user.submissions_count || 0,
              submission_xp: user.submission_xp || 0,
              referral_xp: user.referral_xp || 0,
              bonus_xp: user.bonus_xp || 0,
              actual_total_xp: user.total_xp || 0 // Keep actual total for reference
            }))
            .sort((a, b) => b.totalXP - a.totalXP);
          break;

        case 'referral':
          console.log('Fetching Referral leaderboard data...');
          const referralResponse = await apiService.getLeaderboard('all', 100, 0, 'referral');
          const referralData = Array.isArray(referralResponse) ? referralResponse : referralResponse.data || [];

          categoryData = referralData
            .filter(user => !user.banned && user.referral_xp > 0)
            .map(user => ({
              id: user.id || user.telegram_id,
              username: user.username,
              total_xp: user.referral_xp || 0, // Display referral XP only
              totalXP: user.referral_xp || 0,
              submissions: user.submissions_count || 0,
              submissions_count: user.submissions_count || 0,
              submission_xp: user.submission_xp || 0,
              referral_xp: user.referral_xp || 0,
              bonus_xp: user.bonus_xp || 0,
              actual_total_xp: user.total_xp || 0 // Keep actual total for reference
            }))
            .sort((a, b) => b.totalXP - a.totalXP);
          break;

        default:
          categoryData = [];
      }
      
      setLeaderboardData(prev => ({
        ...prev,
        [categoryId]: categoryData
      }));
      
      console.log(`${categoryId} data loaded with ${categoryData.length} users`);
    } catch (error) {
      console.error(`Error loading ${categoryId} data:`, error);
      setError(`Failed to load ${categoryId} data: ${error.message}`);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleUserClick = async (user) => {
    console.log('User clicked:', user);
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching details for user ID:', user.id);
      
      const apiResponse = await apiService.getUserDetails(user.id);
      console.log('Raw API response:', apiResponse);
      
      if (apiResponse) {
        // Process the API response to create the structure UserProfile expects
        const processedUser = await processUserDetailsResponse(apiResponse);
        console.log('Processed user data:', processedUser);
        setSelectedUser(processedUser);
      } else {
        setError('Failed to load user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError(`Failed to load user details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function to App.js
  const processUserDetailsResponse = async (apiResponse) => {
    const { user, submissions = [], stats, bonuses = [] } = apiResponse;
    
    // Calculate category breakdown from submissions
    let retweetXp = 0, twitterXp = 0, commentXp = 0, cmcXp = 0;
    let retweetSubs = 0, twitterSubs = 0, commentSubs = 0, cmcSubs = 0;

    submissions.forEach((submission) => {
      const finalXp = Number(submission.final_xp || 0);
      const platform = submission.platform?.toLowerCase();
      const category = submission.category?.toLowerCase();

      if (category === 'retweet' || submission.comment_target_account === 'RETWEET_MARKER') {
        retweetXp += finalXp;
        retweetSubs++;
      } else if (platform === 'twitter' || category === 'twitter') {
        twitterXp += finalXp;
        twitterSubs++;
      } else if (platform === 'comment' || category === 'comment') {
        commentXp += finalXp;
        commentSubs++;
      } else if (platform === 'cmc' || category === 'cmc') {
        cmcXp += finalXp;
        cmcSubs++;
      }
    });

    // Use stats for submission counts if available
    const finalRetweetSubs = stats?.retweet_submissions || retweetSubs;
    const finalTwitterSubs = stats?.twitter_submissions || twitterSubs;
    const finalCommentSubs = stats?.comment_submissions || commentSubs;
    const finalCmcSubs = stats?.cmc_submissions || cmcSubs;

    return {
      // Basic user info
      id: user.id,
      telegram_id: user.telegram_id,
      username: user.username,
      submission_xp: user.submission_xp || 0,
      total_xp: user.total_xp || 0,
      referral_xp: user.referral_xp || 0,
      bonus_xp: user.bonus_xp || 0,
      submissions_count: user.submissions_count || 0,
      
      // Legacy compatibility
      totalXP: user.total_xp || 0,
      submissionsXP: user.submission_xp || 0,
      referralXP: user.referral_xp || 0,
      bonusXP: user.bonus_xp || 0,
      
      // Category breakdown that UserProfile expects
      retweet: { xp: retweetXp, submissions: finalRetweetSubs },
      twitter: { xp: twitterXp, submissions: finalTwitterSubs },
      comment: { xp: commentXp, submissions: finalCommentSubs },
      cmc: { xp: cmcXp, submissions: finalCmcSubs },
      
      bonuses: bonuses
    };
  };

  const handleBackToLeaderboard = () => {
    setSelectedUser(null);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Get search results and full leaderboard data in parallel to calculate real ranks
      const [searchResponse, leaderboardResponse] = await Promise.all([
        apiService.getUsers(searchTerm, 20, 0),
        apiService.getLeaderboard('all', 1000, 0, activeCategory === 'general' ? 'general' :
          activeCategory === 'yourpost' ? 'twitter_bonus' :
          activeCategory === 'comment' ? 'comment_bonus' :
          activeCategory === 'cmc' ? 'cmc' :
          activeCategory === 'referral' ? 'referral' : 'general')
      ]);
      
      // Get the leaderboard data for ranking
      const fullLeaderboard = Array.isArray(leaderboardResponse) ? leaderboardResponse : leaderboardResponse.data || [];
      
      // Create a ranking map based on current category
      const rankingMap = new Map();
      fullLeaderboard
        .filter(user => !user.banned)
        .sort((a, b) => {
          // Sort by category-specific XP or total XP
          const aXP = activeCategory === 'general' ? (a.total_xp || 0) : (a.submission_xp || 0);
          const bXP = activeCategory === 'general' ? (b.total_xp || 0) : (b.submission_xp || 0);
          return bXP - aXP;
        })
        .forEach((user, index) => {
          const userId = user.id || user.telegram_id;
          rankingMap.set(userId, index + 1);
        });
      
      // Transform search results with real ranks
      const transformedResults = searchResponse.map(user => {
        const userId = user.id || user.telegram_id;
        const realRank = rankingMap.get(userId) || 'N/A';
        
        return {
          // New User interface fields
          id: userId,
          telegram_id: user.telegram_id,
          username: user.username || `User_${user.id}`,
          total_xp: user.total_xp || 0,
          referral_xp: user.referral_xp || 0,
          bonus_xp: user.bonus_xp || 0,
          submissions_count: user.submissions_count || user.submission_count || 0,
          banned: user.banned || false,
          
          // Legacy compatibility fields
          totalXP: user.total_xp || 0,
          submissions: user.submissions_count || user.submission_count || 0,
          referralXP: user.referral_xp || 0,
          bonusXP: user.bonus_xp || 0,
          
          // Real rank from leaderboard
          realRank: realRank
        };
      })
      .filter(user => !user.banned) // Filter out banned users
      .sort((a, b) => {
        // Sort search results by their real rank
        if (a.realRank === 'N/A') return 1;
        if (b.realRank === 'N/A') return -1;
        return a.realRank - b.realRank;
      });
      
      setSearchResults(transformedResults);
      setIsSearchMode(true);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error searching users:', error);
      setError(`Search failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearchMode(false);
    setSearchResults([]);
    setCurrentPage(1);
  };

  // Function to load more data for pagination (for future use)
  // eslint-disable-next-line no-unused-vars
  const loadMoreData = async (category = activeCategory, page = currentPage) => {
    if (loading || !hasMoreData) return;
    
    try {
      setLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const response = await apiService.getLeaderboard('all', itemsPerPage, offset,
        category === 'general' ? 'general' :
        category === 'yourpost' ? 'twitter_bonus' :
        category === 'comment' ? 'comment_bonus' :
        category === 'cmc' ? 'cmc' :
        category === 'referral' ? 'referral' : 'general'
      );
      
      const newData = Array.isArray(response) ? response : response.data || [];
      
      if (newData.length < itemsPerPage) {
        setHasMoreData(false);
      }
      
      const transformedData = newData
        .filter(user => !user.banned)
        .map(user => ({
          id: user.id || user.telegram_id,
          username: user.username,
          total_xp: category === 'general' ? (user.total_xp || 0) : (user.submission_xp || 0), // Show category-specific XP for categories
          totalXP: category === 'general' ? (user.total_xp || 0) : (user.submission_xp || 0),
          submissions: user.submissions_count || 0,
          submissions_count: user.submissions_count || 0,
          submission_xp: user.submission_xp || 0,
          referral_xp: user.referral_xp || 0,
          bonus_xp: user.bonus_xp || 0,
          actual_total_xp: user.total_xp || 0 // Keep actual total for reference
        }));

      if (page === 1) {
        setLeaderboardData(prev => ({
          ...prev,
          [category]: transformedData
        }));
      } else {
        setLeaderboardData(prev => ({
          ...prev,
          [category]: [...(prev[category] || []), ...transformedData]
        }));
      }
    } catch (error) {
      console.error('Error loading more data:', error);
      setError(`Failed to load more data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
    setHasMoreData(true);
    if (isSearchMode) {
      setIsSearchMode(false);
      setSearchResults([]);
    }
    
    // Load category data if not already loaded
    await loadCategoryIfNeeded(categoryId);
  };

  // Get current data for display (search results or leaderboard)
  const getCurrentData = () => {
    if (isSearchMode) {
      return searchResults;
    }
    return leaderboardData[activeCategory] || [];
  };

  // Pagination logic
  const currentData = getCurrentData();
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = currentData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const isRafflePage = location.pathname === '/raffle';
  const isLeaderboardPage = location.pathname === '/';

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <h1>Squire Leaderboard</h1>
        </div>
        <nav className="main-nav">
          <Link
            to="/"
            className={`nav-btn ${isLeaderboardPage ? 'active' : ''}`}
            onClick={() => setSelectedUser(null)}
          >
            Leaderboard
          </Link>
          <Link
            to="/raffle"
            className={`nav-btn ${isRafflePage ? 'active' : ''}`}
            onClick={() => setSelectedUser(null)}
          >
            Raffle
          </Link>
        </nav>
        <div className="reset-notice">
          <span>Database resets bi-weekly</span>
        </div>
      </header>

      <Routes>
        <Route path="/raffle" element={
          <main className="main-content">
            <Raffle />
          </main>
        } />
        <Route path="/" element={
          selectedUser ? (
            <UserProfile
              user={selectedUser}
              onBack={handleBackToLeaderboard}
            />
          ) : (
            <>
              {error && (
                <div className="error-banner">
                  <span>Warning: {error}</span>
                  <button onClick={fetchLeaderboardData} className="retry-btn">Retry</button>
                </div>
              )}
        <main className="main-content">
          <div className="pool-explanation">
            <p>Every pool holds a prize, XP decides the size — the more you hold, the more you'll rise.</p>
          </div>
          
          {/* Search Bar */}
          <div className="search-section">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by Telegram username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
              <button onClick={handleSearch} className="search-btn" disabled={loading}>
                {loading ? 'Loading...' : 'Search'}
              </button>
              {isSearchMode && (
                <button onClick={handleClearSearch} className="clear-btn">
                  Clear
                </button>
              )}
            </div>
            {isSearchMode && (
              <div className="search-info">
                Showing {totalItems} search results for "{searchTerm}" (with real general leaderboard ranks)
              </div>
            )}
          </div>
          
          {!isSearchMode && (
            <nav className="category-nav">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </nav>
          )}

          <Leaderboard
            category={isSearchMode ? 'search' : activeCategory}
            data={currentItems}
            loading={loading || categoryLoading}
            onUserClick={handleUserClick}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            isSearchMode={isSearchMode}
          />

          {/* Category loading indicator */}
          {categoryLoading && (
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              margin: '1rem 0',
              color: '#667eea',
              fontWeight: '600'
            }}>
              Loading category data...
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                ← Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next →
              </button>
            </div>
          )}
        </main>
            </>
          )
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;